import axios from "axios";

// Resolve charger API base. Prefer explicit `VITE_CHARGER_API_URL`,
// then `VITE_VEHICLE_API_URL` or `VITE_API_BASE_URL` derivations, otherwise
// fall back to the vehicle-service charger mount used by the backend: `/api/v1/chargers`.
const CHARGER_API_URL =
  (import.meta.env.VITE_CHARGER_API_URL as string) ||
  (() => {
    const vVehicle = import.meta.env.VITE_VEHICLE_API_URL as string;
    const vBase = import.meta.env.VITE_API_BASE_URL as string;

    if (vVehicle) {
      // If the vehicle URL contains `/vehicles`, replace it with `/v1/chargers`
      if (vVehicle.includes("/vehicles")) {
        return vVehicle.replace(/\/vehicles\/?$/, "/v1/chargers");
      }
      // Otherwise, assume it's a host/root and append the service path
      return vVehicle.replace(/\/$/, "") + "/v1/chargers";
    }

    if (vBase) return vBase.replace(/\/$/, "") + "/v1/chargers";

    return "/api/v1/chargers";
  })();

// Debugging helper so devs can see what client is calling
console.debug("[chargerService] CHARGER_API_URL ->", CHARGER_API_URL);

// Configure axios instance
const chargerApi = axios.create({
  baseURL: CHARGER_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
chargerApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
chargerApi.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error("Charger API Error:", error);
    if (error.response?.status === 401) {
      // Handle unauthorized
      const unauthorizedCounter = parseInt(
        sessionStorage.getItem("charger_api_401_count") || "0"
      );
      sessionStorage.setItem(
        "charger_api_401_count",
        (unauthorizedCounter + 1).toString()
      );

      if (unauthorizedCounter >= 5) {
        localStorage.removeItem("authToken");
        localStorage.removeItem("user");
        alert("Your session has expired. You will be redirected to login.");
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Enums matching backend
export enum ChargerType {
  PORTABLE_AC_SINGLE_PHASE = "PORTABLE_AC_SINGLE_PHASE",
  PORTABLE_AC_THREE_PHASE = "PORTABLE_AC_THREE_PHASE",
  PORTABLE_DC_FAST = "PORTABLE_DC_FAST",
  WALL_MOUNTED_AC = "WALL_MOUNTED_AC",
  WALL_MOUNTED_DC = "WALL_MOUNTED_DC",
}

export enum ChargerStatus {
  ACTIVE = "ACTIVE",
  IN_STORAGE = "IN_STORAGE",
  UNDER_REPAIR = "UNDER_REPAIR",
  DAMAGED = "DAMAGED",
  RETIRED = "RETIRED",
}

// Interfaces
export interface Charger {
  id: string;
  chargerNumber: string;
  parentVehicleId: string;
  currentVehicleId?: string;
  manufacturer: string;
  chargerType: ChargerType;
  powerOutput: number;
  inputVoltage?: string;
  outputVoltage?: string;
  efficiency?: number;
  cableLength?: number;
  connectorType?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  purchaseDate?: string;
  status: ChargerStatus;
  healthPercentage?: number;
  chargingCycles: number;
  lastUsedDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  parentVehicle?: any;
  currentVehicle?: any;
  swapHistory?: ChargerSwapHistoryRecord[];
}

export interface ChargerSwapHistoryRecord {
  id: string;
  chargerId: string;
  fromVehicleId?: string;
  toVehicleId?: string;
  swapDate: string;
  swapReason: string;
  performedBy: string;
  remarks?: string;
  charger?: Charger;
  fromVehicle?: any;
  toVehicle?: any;
}

export interface CreateChargerDTO {
  chargerNumber: string;
  parentVehicleId: string;
  currentVehicleId?: string;
  manufacturer: string;
  chargerType: ChargerType;
  powerOutput: number;
  inputVoltage?: string;
  outputVoltage?: string;
  efficiency?: number;
  cableLength?: number;
  connectorType?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  purchaseDate?: string;
  status?: ChargerStatus;
  healthPercentage?: number;
  chargingCycles?: number;
  lastUsedDate?: string;
  notes?: string;
}

export interface UpdateChargerDTO {
  manufacturer?: string;
  chargerType?: ChargerType;
  powerOutput?: number;
  inputVoltage?: string;
  outputVoltage?: string;
  efficiency?: number;
  cableLength?: number;
  connectorType?: string;
  warrantyStartDate?: string;
  warrantyEndDate?: string;
  purchaseDate?: string;
  status?: ChargerStatus;
  healthPercentage?: number;
  chargingCycles?: number;
  lastUsedDate?: string;
  notes?: string;
}

export interface SwapChargerDTO {
  chargerId: string;
  fromVehicleId?: string;
  toVehicleId?: string;
  swapReason: string;
  performedBy: string;
  remarks?: string;
}

export interface ChargerHealthStats {
  total: number;
  excellent: number;
  good: number;
  fair: number;
  poor: number;
  averageHealth: number;
  averageCycles: number;
  inStorage: number;
  active: number;
}

export interface ChargerFilters {
  status?: ChargerStatus;
  currentVehicleId?: string;
  parentVehicleId?: string;
  chargerType?: ChargerType;
  minPowerOutput?: number;
  maxPowerOutput?: number;
}

// API Service
class ChargerServiceClass {
  /**
   * Get all chargers with optional filters
   */
  async getAllChargers(filters?: ChargerFilters): Promise<Charger[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append("status", filters.status);
      if (filters?.currentVehicleId)
        params.append("currentVehicleId", filters.currentVehicleId);
      if (filters?.parentVehicleId)
        params.append("parentVehicleId", filters.parentVehicleId);
      if (filters?.chargerType)
        params.append("chargerType", filters.chargerType);
      if (filters?.minPowerOutput)
        params.append("minPowerOutput", filters.minPowerOutput.toString());
      if (filters?.maxPowerOutput)
        params.append("maxPowerOutput", filters.maxPowerOutput.toString());

      const response = await chargerApi.get(`/?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching chargers:", error);
      throw error;
    }
  }

  /**
   * Get charger by ID
   */
  async getChargerById(id: string): Promise<Charger> {
    try {
      const response = await chargerApi.get(`/${id}`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching charger:", error);
      throw error;
    }
  }

  /**
   * Get charger by charger number
   */
  async getChargerByNumber(chargerNumber: string): Promise<Charger> {
    try {
      const response = await chargerApi.get(`/number/${chargerNumber}`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching charger by number:", error);
      throw error;
    }
  }

  /**
   * Get chargers by vehicle
   */
  async getChargersByVehicle(vehicleId: string): Promise<Charger[]> {
    try {
      const response = await chargerApi.get(`/vehicle/${vehicleId}`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching chargers by vehicle:", error);
      throw error;
    }
  }

  /**
   * Get chargers by parent vehicle
   */
  async getChargersByParentVehicle(
    parentVehicleId: string
  ): Promise<Charger[]> {
    try {
      const response = await chargerApi.get(
        `/parent-vehicle/${parentVehicleId}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching chargers by parent vehicle:", error);
      throw error;
    }
  }

  /**
   * Get available chargers for swapping
   */
  async getAvailableChargers(
    chargerType?: ChargerType,
    minPowerOutput?: number
  ): Promise<Charger[]> {
    try {
      const params = new URLSearchParams();
      if (chargerType) params.append("chargerType", chargerType);
      if (minPowerOutput)
        params.append("minPowerOutput", minPowerOutput.toString());

      const response = await chargerApi.get(`/available?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching available chargers:", error);
      throw error;
    }
  }

  /**
   * Get charger health statistics
   */
  async getChargerHealthStats(): Promise<ChargerHealthStats> {
    try {
      const response = await chargerApi.get("/health-stats");
      return response.data.data;
    } catch (error) {
      console.error("Error fetching charger health stats:", error);
      throw error;
    }
  }

  /**
   * Create new charger
   */
  async createCharger(data: CreateChargerDTO): Promise<Charger> {
    try {
      const response = await chargerApi.post("/", data);
      return response.data.data;
    } catch (error) {
      console.error("Error creating charger:", error);
      throw error;
    }
  }

  /**
   * Update charger
   */
  async updateCharger(id: string, data: UpdateChargerDTO): Promise<Charger> {
    try {
      const response = await chargerApi.put(`/${id}`, data);
      return response.data.data;
    } catch (error) {
      console.error("Error updating charger:", error);
      throw error;
    }
  }

  /**
   * Swap charger
   */
  async swapCharger(
    data: SwapChargerDTO
  ): Promise<{ charger: Charger; swapHistory: ChargerSwapHistoryRecord }> {
    try {
      const response = await chargerApi.post("/swap", data);
      return response.data.data;
    } catch (error) {
      console.error("Error swapping charger:", error);
      throw error;
    }
  }

  /**
   * Get charger swap history
   */
  async getChargerSwapHistory(
    chargerId: string
  ): Promise<ChargerSwapHistoryRecord[]> {
    try {
      const response = await chargerApi.get(`/${chargerId}/swap-history`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching charger swap history:", error);
      throw error;
    }
  }

  /**
   * Get vehicle swap history
   */
  async getVehicleSwapHistory(
    vehicleId: string
  ): Promise<ChargerSwapHistoryRecord[]> {
    try {
      const response = await chargerApi.get(
        `/vehicle/${vehicleId}/swap-history`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching vehicle swap history:", error);
      throw error;
    }
  }

  /**
   * Delete charger (soft delete)
   */
  async deleteCharger(id: string): Promise<Charger> {
    try {
      const response = await chargerApi.delete(`/${id}`);
      return response.data.data;
    } catch (error) {
      console.error("Error deleting charger:", error);
      throw error;
    }
  }

  /**
   * Permanently delete charger
   */
  async permanentlyDeleteCharger(id: string): Promise<void> {
    try {
      await chargerApi.delete(`/${id}/permanent`);
    } catch (error) {
      console.error("Error permanently deleting charger:", error);
      throw error;
    }
  }

  
  /**
   * Update charger status
   */
  async updateChargerStatus(
    chargerNumber: string,
    status: ChargerStatus
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await chargerApi.post("/update-status", {
        chargerNumber,
        status,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating charger status:", error);
      throw error;
    }
  }

}

export const ChargerService = new ChargerServiceClass();
